import type { ReactNode } from "react";

/**
 * Per-tab section header for the Coach Workspace list pages.
 *
 * Ports the legacy `admin-panel-head` / `roster-head` pattern
 * (`index.html`): each coach sub-tab card leads with a
 * DESCRIPTIVE title (`<h3>`) + a sub-line explaining the tab,
 * with the page's action buttons on the right.
 *
 * This is distinct from — and sits below — the shell's constant
 * "Coaching Workspace" header. The tab name is never repeated:
 * the strip already shows which tab is active; this header's job
 * is to describe it.
 */

export type PageHeaderProps = {
  /** Descriptive title — e.g. "Review Playlists", not "Playlists". */
  title: string;
  /** One-line explanation of what this tab is for. */
  description: ReactNode;
  /** Optional action buttons, right-aligned on the same row. */
  actions?: ReactNode;
};

export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <div className="flex flex-wrap items-start gap-3">
      <div className="min-w-0 flex-1">
        <h2 className="text-lg font-display text-ink">{title}</h2>
        <p className="text-ink-muted text-sm mt-0.5">{description}</p>
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}
